// we need getUsersByFilter, getUserOptionsByFilter, getActiveUserOptions, getAllUserOptions, getOfficesByFilter, getLeadSourcesByFilter

import { useAuthFetch } from "@/hooks/data/useAuthFetch";
import { userQueryKeys } from "@/hooks/data/v2/query-keys";
import { parseResponse } from "@/lib/query-helper";
import { APIResponse, PaginatedResponse, SearchFilter } from "@simplerealtybits/srb-types";
import { SelectOptionDto } from "@t/SelectOptionDto";
import { UserGet } from "@t/UserGet";
import { UserUpdate } from "@t/UserUpdate";
import { UserSelfUpdate } from "@t/UserSelfUpdate";
import { UserCreateDto } from "@t/UserCreateDto";
import { UploadAvatarImageRequestDto } from "@t/Common/Dtos/Users/UploadAvatarImageRequestDto";
import {
    QueryClient,
    UndefinedInitialDataOptions,
    useMutation,
    UseMutationOptions,
    useQuery,
} from "@tanstack/react-query";
import { UserSelectOption } from "@t/UserSelectOption";
import { useCallback } from "react";
import { UserSearchFilter } from "@t/Common/Filters/UserSearchFilter";

export function useGetUsersByFilter(filter: SearchFilter) {
    const authFetch = useAuthFetch();

    const getUsersByFilter = async (
        filter: SearchFilter
    ): Promise<PaginatedResponse<UserSelectOption>> => {
        try {
            const res = await authFetch("/api/v1/users/search", {
                method: "POST",
                body: JSON.stringify(filter),
            });
            const resJson: PaginatedResponse<UserSelectOption> = await res.json();

            if (!resJson.success) throw new Error(resJson.message);

            return resJson;
        } catch (error) {
            console.error(error);
            throw error;
        }
    };

    const getUsersByFilterQueryOptions = () => ({
        queryKey: userQueryKeys.getByFilter(filter),
        queryFn: () => getUsersByFilter(filter),
        enabled: !!filter,
        select: (data: PaginatedResponse<UserSelectOption>) => data.data,
    });

    return useQuery(getUsersByFilterQueryOptions());
}

// Hook that returns the fetch function for use with RemoteDataTable
export function useUsersByFilterFetch() {
    const authFetch = useAuthFetch();

    const getUsersByFilter = useCallback(
        async (filter: SearchFilter): Promise<PaginatedResponse<UserSelectOption>> => {
            try {
                const res = await authFetch("/api/v1/users/search", {
                    method: "POST",
                    body: JSON.stringify(filter),
                });
                const resJson: PaginatedResponse<UserSelectOption> = await res.json();

                if (!resJson.success) throw new Error(resJson.message);

                return resJson;
            } catch (error) {
                console.error(error);
                throw error;
            }
        },
        [authFetch]
    );

    return { getUsersByFilter };
}

export function useUserSearchFull(filter: UserSearchFilter) {
    const authFetch = useAuthFetch();

    return useQuery({
        queryKey: userQueryKeys.getFullByFilter(filter),
        queryFn: async (): Promise<PaginatedResponse<UserGet>> => {
            const res = await authFetch("/api/v1/users/search-filter", {
                method: "POST",
                body: JSON.stringify(filter),
            });
            const resJson: PaginatedResponse<UserGet> = await res.json();

            if (!resJson.success) throw new Error(resJson.message);

            return resJson;
        },
    });
}

// we now need to follow the same hook pattern above for the other user-related queries
export function useGetUserOptionsByFilter(filter: SearchFilter) {
    const authFetch = useAuthFetch();

    const getUserOptionsByFilter = async (
        filter: SearchFilter
    ): Promise<APIResponse<SelectOptionDto[]>> => {
        try {
            const res = await authFetch("/api/v1/users/search-user-options", {
                method: "POST",
                body: JSON.stringify(filter),
            });
            const resJson: APIResponse<SelectOptionDto[]> = await res.json();

            if (!resJson.success) throw new Error(resJson.message);

            return resJson;
        } catch (error) {
            console.error(error);
            throw error;
        }
    };

    const getUserOptionsByFilterQueryOptions = () => ({
        queryKey: userQueryKeys.getOptionsByFilter(filter),
        queryFn: () => getUserOptionsByFilter(filter),
        enabled: !!filter,
        select: (data: APIResponse<SelectOptionDto[]>) => data.data,
    });

    return useQuery(getUserOptionsByFilterQueryOptions());
}

export const useGetActiveUserOptions = () => {
    const authFetch = useAuthFetch();

    const getActiveUserOptions = async (): Promise<APIResponse<SelectOptionDto[]>> => {
        try {
            const res = await authFetch("/api/v1/users/user-options?includeNonActiveUsers=false");
            const resJson: APIResponse<SelectOptionDto[]> = await res.json();

            if (!resJson.success) throw new Error(resJson.message);

            return resJson;
        } catch (error) {
            console.error(error);
            throw error;
        }
    };

    const getActiveUserOptionsQueryOptions = () => ({
        queryKey: userQueryKeys.getActiveOptions(),
        queryFn: getActiveUserOptions,
        select: (data: APIResponse<SelectOptionDto[]>) => data.data,
    });

    return useQuery(getActiveUserOptionsQueryOptions());
};

export const useGetAllUserOptions = () => {
    const authFetch = useAuthFetch();

    const getAllUserOptions = async (): Promise<APIResponse<SelectOptionDto[]>> => {
        try {
            const res = await authFetch("/api/v1/users/user-options?includeNonActiveUsers=true");
            const resJson: APIResponse<SelectOptionDto[]> = await res.json();
            if (!resJson.success) throw new Error(resJson.message);
            return resJson;
        } catch (error) {
            console.error(error);
            throw error;
        }
    };
    const getAllUserOptionsQueryOptions = () => ({
        queryKey: userQueryKeys.getAllOptions(),
        queryFn: getAllUserOptions,
        select: (data: APIResponse<SelectOptionDto[]>) => data.data,
    });
    return useQuery(getAllUserOptionsQueryOptions());
};

type UseGetUserQueryOptions = Partial<
    UndefinedInitialDataOptions<
        APIResponse<UserGet>,
        Error,
        UserGet,
        readonly ["users", number | null]
    >
>;

export const useGetUserQuery = (
    userId: number,
    options?: UseGetUserQueryOptions,
    queryClient?: QueryClient
) => {
    const authFetch = useAuthFetch();

    const queryFn = async (): Promise<APIResponse<UserGet>> => {
        const res = await authFetch(`/api/v1/users/${userId}`);
        return await parseResponse(res);
    };

    return useQuery(
        {
            ...options,
            queryKey: userQueryKeys.getById(userId),
            queryFn: queryFn,
            select: (data) => data.data,
        },
        queryClient
    );
};

export const useUpdateUserMutation = (
    options?: Partial<UseMutationOptions<APIResponse<UserGet>, Error, UserUpdate>>,
    queryClient?: QueryClient
) => {
    const authFetch = useAuthFetch();

    const mutationOptions: UseMutationOptions<APIResponse<UserGet>, Error, UserUpdate> = {
        ...options,
        mutationFn: async (user: UserUpdate) => {
            const parsedUser: UserUpdate = {
                ...user,
                officeId: parseInt(user.officeId?.toString() ?? "0"),
                companyId: parseInt(user.companyId?.toString() ?? "0"),
                userRole: parseInt(user.userRole.toString()),
            };
            const response = await authFetch(`/api/v1/users/update`, {
                method: "PUT",
                body: JSON.stringify(parsedUser),
            });
            return await parseResponse(response);
        },
    };

    return useMutation(mutationOptions, queryClient);
};

export const useUpdateMyAccountMutation = (
    options?: Partial<UseMutationOptions<APIResponse<UserGet>, Error, UserSelfUpdate>>,
    queryClient?: QueryClient
) => {
    const authFetch = useAuthFetch();

    const mutationOptions: UseMutationOptions<APIResponse<UserGet>, Error, UserSelfUpdate> = {
        ...options,
        mutationFn: async (user: UserSelfUpdate) => {
            const response = await authFetch(`/api/v1/users/update-my-account`, {
                method: "PUT",
                body: JSON.stringify(user),
            });
            return await parseResponse(response);
        },
    };

    return useMutation(mutationOptions, queryClient);
};

export const useRegisterUserMutation = (
    options?: Partial<UseMutationOptions<APIResponse<UserGet>, Error, UserCreateDto>>,
    queryClient?: QueryClient
) => {
    const authFetch = useAuthFetch();

    const mutationOptions: UseMutationOptions<APIResponse<UserGet>, Error, UserCreateDto> = {
        ...options,
        mutationFn: async (user: UserCreateDto) => {
            const parsedUser = {
                ...user,
                officeId: parseInt(user.officeId?.toString() ?? "0"),
                userRole: parseInt(user.userRole.toString()),
            };
            const response = await authFetch(`/api/v1/users/create`, {
                method: "POST",
                body: JSON.stringify(parsedUser),
            });
            return await parseResponse(response);
        },
    };

    return useMutation(mutationOptions, queryClient);
};

export const useCreateWiseTackMerchantUserMutation = (
    options?: Partial<UseMutationOptions<APIResponse<UserGet>, Error, number>>,
    queryClient?: QueryClient
) => {
    const authFetch = useAuthFetch();

    const mutationOptions: UseMutationOptions<APIResponse<UserGet>, Error, number> = {
        ...options,
        mutationFn: async (userId: number) => {
            const response = await authFetch(
                `/api/v1/users/create-wisetack-merchant-user/${userId}`,
                {
                    method: "POST",
                }
            );
            return await parseResponse(response);
        },
    };

    return useMutation(mutationOptions, queryClient);
};

export const useDeleteWiseTackMerchantUserMutation = (
    options?: Partial<UseMutationOptions<APIResponse<UserGet>, Error, number>>,
    queryClient?: QueryClient
) => {
    const authFetch = useAuthFetch();

    const mutationOptions: UseMutationOptions<APIResponse<UserGet>, Error, number> = {
        ...options,
        mutationFn: async (userId: number) => {
            const response = await authFetch(
                `/api/v1/users/delete-wisetack-merchant-user/${userId}`,
                {
                    method: "DELETE",
                }
            );
            return await parseResponse(response);
        },
    };

    return useMutation(mutationOptions, queryClient);
};

export const useVerifyMfaCodeAdminMutation = (
    options?: Partial<UseMutationOptions<APIResponse<boolean>, Error, string>>,
    queryClient?: QueryClient
) => {
    const authFetch = useAuthFetch();

    const mutationOptions: UseMutationOptions<APIResponse<boolean>, Error, string> = {
        ...options,
        mutationFn: async (code: string) => {
            const response = await authFetch(`/api/v1/auth/mfa-verify-admin`, {
                method: "POST",
                body: JSON.stringify({ code }),
            });
            return await parseResponse(response);
        },
    };

    return useMutation(mutationOptions, queryClient);
};

export const useResetUserMfaMutation = (
    options?: Partial<UseMutationOptions<APIResponse<boolean>, Error, number>>,
    queryClient?: QueryClient
) => {
    const authFetch = useAuthFetch();

    const mutationOptions: UseMutationOptions<APIResponse<boolean>, Error, number> = {
        ...options,
        mutationFn: async (userId: number) => {
            const response = await authFetch(`/api/v1/auth/reset-user-mfa`, {
                method: "POST",
                body: JSON.stringify({ userId }),
            });
            return await parseResponse(response);
        },
    };

    return useMutation(mutationOptions, queryClient);
};

export const useUploadAvatarImageMutation = (
    options?: Partial<UseMutationOptions<APIResponse<string>, Error, UploadAvatarImageRequestDto>>,
    queryClient?: QueryClient
) => {
    const authFetch = useAuthFetch();

    const mutationOptions: UseMutationOptions<
        APIResponse<string>,
        Error,
        UploadAvatarImageRequestDto
    > = {
        ...options,
        mutationFn: async (avatarUploadRequest: UploadAvatarImageRequestDto) => {
            const formData = new FormData();
            formData.append(
                "avatarImageFile",
                avatarUploadRequest.avatarImageFile as unknown as Blob
            );
            formData.append("userId", avatarUploadRequest.userId.toString());

            const response = await authFetch(`/api/v1/users/upload-avatar-image`, {
                method: "POST",
                body: formData,
            });
            return await parseResponse(response);
        },
    };

    return useMutation(mutationOptions, queryClient);
};

export const useGetUserManagersQuery = () => {
    const authFetch = useAuthFetch();

    const getUserManagers = async (): Promise<APIResponse<UserGet[]>> => {
        const res = await authFetch("/api/v1/users/get-managers");
        return await parseResponse(res);
    };

    const getUserManagersQueryOptions = () => ({
        queryKey: userQueryKeys.getManagers(),
        queryFn: getUserManagers,
        select: (data: APIResponse<UserGet[]>) => data.data,
    });

    return useQuery(getUserManagersQueryOptions());
};

export const useGetOutsideSalesPersonnel = () => {
    const authFetch = useAuthFetch();

    const getOutsideSalesPersonnel = async (): Promise<APIResponse<UserGet[]>> => {
        const res = await authFetch("/api/v1/users/get-outside-sales");
        return await parseResponse(res);
    };

    const getOutsideSalesPersonnelQueryOptions = () => ({
        queryKey: userQueryKeys.getOutsideSalesPersonnel(),
        queryFn: getOutsideSalesPersonnel,
        select: (data: APIResponse<UserGet[]>) => data.data,
    });

    return useQuery(getOutsideSalesPersonnelQueryOptions());
};

export const useDeactivateUserMutation = (
    options?: Partial<UseMutationOptions<APIResponse<UserGet>, Error, number>>,
    queryClient?: QueryClient
) => {
    const authFetch = useAuthFetch();

    const mutationOptions: UseMutationOptions<APIResponse<UserGet>, Error, number> = {
        ...options,
        mutationFn: async (id: number) => {
            const response = await authFetch(`/api/v1/users/deactivate/${id}`, {
                method: "POST",
            });
            return await parseResponse(response);
        },
    };

    return useMutation(mutationOptions, queryClient);
};

export const useActivateUserMutation = (
    options?: Partial<UseMutationOptions<APIResponse<UserGet>, Error, number>>,
    queryClient?: QueryClient
) => {
    const authFetch = useAuthFetch();

    const mutationOptions: UseMutationOptions<APIResponse<UserGet>, Error, number> = {
        ...options,
        mutationFn: async (id: number) => {
            const response = await authFetch(`/api/v1/users/reactivate/${id}`, {
                method: "POST",
            });
            return await parseResponse(response);
        },
    };

    return useMutation(mutationOptions, queryClient);
};

export const useSyncLegacyLinksMutation = (
    options?: Partial<UseMutationOptions<APIResponse<UserGet>, Error, number>>,
    queryClient?: QueryClient
) => {
    const authFetch = useAuthFetch();

    const mutationOptions: UseMutationOptions<APIResponse<UserGet>, Error, number> = {
        ...options,
        mutationFn: async (userId: number) => {
            const response = await authFetch(`/api/v1/users/sync-legacy-links/${userId}`, {
                method: "POST",
            });
            return await parseResponse(response);
        },
    };

    return useMutation(mutationOptions, queryClient);
};

export const useCreateFieldRoutesUserMutation = (
    options?: Partial<
        UseMutationOptions<APIResponse<UserGet>, Error, { userId: number; officeId: number }>
    >,
    queryClient?: QueryClient
) => {
    const authFetch = useAuthFetch();

    const mutationOptions: UseMutationOptions<
        APIResponse<UserGet>,
        Error,
        { userId: number; officeId: number }
    > = {
        ...options,
        mutationFn: async ({ userId, officeId }: { userId: number; officeId: number }) => {
            const response = await authFetch(
                `/api/v1/users/create-field-routes-user/${userId}?officeId=${officeId}`,
                {
                    method: "POST",
                }
            );
            return await parseResponse(response);
        },
    };

    return useMutation(mutationOptions, queryClient);
};

export interface BulkCreateFieldRoutesUsersRequest {
    userIds: number[];
    jobId?: string;
}

export enum BulkFieldRoutesUserStatus {
    Success = 0,
    Warning = 1,
    Failed = 2,
}

export interface BulkFieldRoutesProgressItem {
    jobId: string;
    index: number;
    total: number;
    userId: number;
    userFullName: string;
    status: BulkFieldRoutesUserStatus;
    message: string;
}

export interface BulkFieldRoutesSummaryDto {
    jobId: string;
    totalProcessed: number;
    successCount: number;
    warningCount: number;
    failureCount: number;
    items: BulkFieldRoutesProgressItem[];
}

export const useBulkCreateFieldRoutesUsersMutation = (
    options?: Partial<
        UseMutationOptions<
            APIResponse<BulkFieldRoutesSummaryDto>,
            Error,
            BulkCreateFieldRoutesUsersRequest
        >
    >,
    queryClient?: QueryClient
) => {
    const authFetch = useAuthFetch();

    const mutationOptions: UseMutationOptions<
        APIResponse<BulkFieldRoutesSummaryDto>,
        Error,
        BulkCreateFieldRoutesUsersRequest
    > = {
        ...options,
        mutationFn: async (request: BulkCreateFieldRoutesUsersRequest) => {
            const response = await authFetch(`/api/v1/users/bulk-create-field-routes-users`, {
                method: "POST",
                body: JSON.stringify(request),
            });
            return await parseResponse(response);
        },
    };

    return useMutation(mutationOptions, queryClient);
};
